import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { usePasswordValidation } from '@/hooks/usePasswordValidation';
import { useAuthStore } from '@store/authStore';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { Label } from '@components/ui/Label';
import { Alert, AlertDescription } from '@components/ui/Alert';
import PasswordInput from '@components/forms/PasswordInput';
import { RiAlertLine, RiArrowRightLine } from "@remixicon/react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { setAuthType } = useAuthStore();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  // MFA state
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [showMFAStep, setShowMFAStep] = useState(false);

  // Password validation using custom hook
  const { validation: passwordValidation, isValid: isPasswordValid } = usePasswordValidation(password);

  useEffect(() => {
    // Check for reset code in URL and validate it
    const validateResetCode = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        if (!code) {
          setError('Invalid password reset link. No reset code found.');
          setTokenValid(false);
          setValidatingToken(false);
          return;
        }

        // The presence of a code parameter indicates this is a valid reset link
        // Supabase will handle the code validation when we attempt to update the password
        setTokenValid(true);

        // Check if user has MFA enabled by attempting to get session
        // This might not work until after password reset, so we'll handle MFA during the reset process
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const { data: factors } = await supabase.auth.mfa.listFactors();
            if (factors && factors.totp && factors.totp.length > 0) {
              setRequiresMFA(true);
              setMfaFactorId(factors.totp[0].id);
            }
          }
        } catch (mfaError) {
          // If MFA check fails, continue without MFA requirement for now
          console.log('MFA check skipped - will handle during password reset if needed');
        }

      } catch (err) {
        console.error('Error validating reset code:', err);
        setError('Error validating reset link.');
        setTokenValid(false);
      } finally {
        setValidatingToken(false);
      }
    };

    validateResetCode();
  }, []);

  const doPasswordsMatch = password === confirmPassword && confirmPassword !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (!isPasswordValid) {
      setError('Password does not meet all requirements');
      setLoading(false);
      return;
    }

    // If MFA is required, show MFA step instead of updating password
    if (requiresMFA && !showMFAStep) {
      setShowMFAStep(true);
      setLoading(false);
      return;
    }

    try {
      // If MFA is required, verify MFA code first to get AAL2 session
      if (requiresMFA && showMFAStep) {
        if (!mfaCode || mfaCode.length !== 6) {
          setError('Please enter a valid 6-digit MFA code');
          setLoading(false);
          return;
        }

        // Create MFA challenge
        const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
          factorId: mfaFactorId,
        });

        if (challengeError) throw challengeError;

        // Verify MFA code
        const { error: verifyError } = await supabase.auth.mfa.verify({
          factorId: mfaFactorId,
          challengeId: challengeData.id,
          code: mfaCode,
        });

        if (verifyError) throw verifyError;
      }

      // Now update the password (with AAL2 session if MFA was required)
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      // Success
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (success) {
      setAuthType('signin');
      navigate('/');
    } else {
      navigate('/');
      setAuthType('signin');
    }
  };

  if (validatingToken) {
    return (
      <>
        <div className="text-center lg:text-left mb-6">
          <h2 className="text-3xl font-bold text-primary-500">Reset Password</h2>
        </div>
        
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
        
        <p className="text-center text-gray-600">Validating reset link...</p>
      </>
    );
  }

  if (!tokenValid) {
    return (
      <>
        <div className="text-center lg:text-left mb-6">
          <h2 className="text-3xl font-bold text-primary-500">Invalid Reset Link</h2>
        </div>
        
        {error && (
          <Alert variant="destructive" className="border-red-200 bg-red-50 mb-4">
            <RiAlertLine className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <p className="text-gray-700 mb-6">
          This password reset link is invalid or has expired. Please request a new one.
        </p>
        
        <div className="flex space-x-4 mt-auto pt-6">
          <Button 
            type="button" 
            variant="outline" 
            className="flex-1 !border-border-light !font-normal"
            onClick={() => navigate('/forgot-password')}
          >
            Request New Link
          </Button>
          <Button 
            type="button"
            className="flex-1 !font-normal bg-primary-500 hover:bg-primary-600"
            onClick={handleCancel}
          >
            Return to Sign In
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="text-center lg:text-left mb-6">
        <h2 className="text-3xl font-bold text-primary-500">
          {success ? 'Password Reset Successfully' : showMFAStep ? 'Verify MFA' : 'Reset Password'}
        </h2>
      </div>

      {error && (
        <Alert variant="destructive" className="border-red-200 bg-red-50 mb-4">
          <RiAlertLine className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success ? (
        <div className="space-y-4">
          <p className="text-gray-700">
            Your password has been reset successfully!
          </p>
          <p className="text-gray-600">
            You can now sign in with your new password.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {showMFAStep ? (
            <p className="text-gray-700">
              Your account has MFA enabled. Please enter your authenticator code to continue.
            </p>
          ) : (
            <p className="text-gray-700">
              Please enter your new password below.
            </p>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {showMFAStep ? (
                <div className="space-y-2">
                  <Label htmlFor="mfaCode">
                    Authenticator Code <span className="text-[#0C3E71]"> *</span>
                  </Label>
                  <Input
                    id="mfaCode"
                    type="text"
                    placeholder="000000"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    maxLength={6}
                    disabled={loading}
                    className="h-11 !bg-white !border-2 !border-border-light !text-slate-500 !rounded-lg text-center font-mono"
                    autoFocus
                  />
                  <p className="text-sm text-gray-600">Enter the 6-digit code from your authenticator app</p>
                </div>
              ) : (
                <>
                  <PasswordInput
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    label="New Password"
                    required
                    disabled={loading}
                    showValidation={true}
                  />

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      Confirm New Password <span className="text-[#0C3E71]"> *</span>
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="h-11 !bg-white !border-2 !border-border-light !text-slate-500 !rounded-lg"
                    />
                    {confirmPassword && (
                      <p className={`text-sm ${password === confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                        {password === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                      </p>
                    )}
                  </div>
                </>
              )}
            </form>
          )}
        </div>
      )}

      <div className="flex space-x-4 mt-auto pt-6">
        <Button 
          type="button" 
          variant="secondary"
          mode="stroke"
          className="flex-1 !border-border-light !font-normal"
          disabled={loading}
          onClick={showMFAStep ? () => {
            setShowMFAStep(false);
            setMfaCode('');
            setError(null);
          } : handleCancel}
        >
          {success ? 'Continue' : showMFAStep ? 'Back' : 'Cancel'}
        </Button>
        
        {!success && (
          <Button 
            type="button"
            className="flex-1 !font-normal bg-primary-500 hover:bg-primary-600 disabled:bg-[#F7F7F7] disabled:border disabled:border-border-light disabled:text-gray-400"
            disabled={loading || (showMFAStep ? !mfaCode || mfaCode.length !== 6 : !password || !confirmPassword || password !== confirmPassword)}
            onClick={handleSubmit}
          >
            {loading ? (showMFAStep ? 'Verifying...' : 'Resetting...') : (showMFAStep ? 'Verify & Reset' : 'Reset Password')} 
            {!loading && <RiArrowRightLine className="inline-block ml-1" />}
          </Button>
        )}
      </div>
    </>
  );
}
